// ProfileFilters.js
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import {
  saveToState,
  loadFromState,
  resetState,
  formateTimeRange,
} from "../utils";
import { getProfiles, getNodes, getProfilerMetadata } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

const ProfileFilter = ({ isLoading, propagnateData }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const [activeNode, setActiveNode] = useState(null); // {label, value}
  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);

  const [timestampBuckets, setTimestampBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  // ---------------------------
  // Node search
  // ---------------------------

  const toOption = (node) => ({ label: node.name, value: node.uuid });

  const fetchEntitiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NAMED_ENTITY:ORGANISATION",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const fetchProxiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NODE_SET",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadNodeOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const [entities, proxies] = await Promise.all([
        fetchEntitiesPage({ search: q, page }),
        fetchProxiesPage({ search: q, page }),
      ]);

      const options = [...entities, ...proxies].map(toOption);
      const hasMore =
        entities.length === ITEMS_PER_PAGE || proxies.length === ITEMS_PER_PAGE;

      return { options, hasMore, additional: { page: page + 1 } };
    },
    [fetchEntitiesPage, fetchProxiesPage]
  );

  const nodeAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Load profile + buckets
  // ---------------------------

  const loadProfile = useCallback(async () => {
    if (!activeNode?.value) return;

    // 1) load profile
    const profiles = await getProfiles({
      itemsPerPage: 1,
      entities: [activeNode.value],
    });

    propagnateFunctionRef.current(profiles.data);

    const row = profiles?.data?.data?.[0] ?? null;
    setData(row);

    // 2) load buckets
    setLoadingBuckets(true);
    try {
      const metaRes = await getProfilerMetadata({
        itemsPerPage: 1,
        entities: [activeNode.value],
      });

      const metaRow = metaRes?.data?.data?.[0] ?? metaRes?.data?.[0] ?? null;
      const intervals = metaRow?.metadata?.corpora?.time_intervals ?? [];

      setTimestampBuckets(intervals);

      // ✅ Auto-select last bucket (unless current selection is still valid)
      const current = activeTimestampBucket;
      const isCurrentValid = current && intervals.includes(current);

      if (isCurrentValid) {
        setActiveTimestampBucket(current);
      } else {
        const last = intervals.length ? intervals[intervals.length - 1] : null;
        setActiveTimestampBucket(last);
      }
    } finally {
      setLoadingBuckets(false);
    }
  }, [activeNode, activeTimestampBucket]);

  // ---------------------------
  // localStorage
  // ---------------------------

  const resetFiltersUI = () => {
    resetState("profileCompareState", [
      { stateFunc: setActiveNode, nullVal: null },
      { stateFunc: setActiveTimestampBucket, nullVal: null },
    ]);
    setData(null);
    setTimestampBuckets([]);
  };

  useEffect(() => {
    loadFromState("profileCompareState", [
      {
        stateFunc: setActiveNode,
        storageObjectKey: "activeNode",
        defaultVal: null,
      },
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => propagnateDataFunctionCopy({});
  }, []);

  useEffect(() => {
    if (activeNode !== null) {
      saveToState("profileCompareState", {
        activeNode,
        activeTimestampBucket,
      });
    }
  }, [activeNode, activeTimestampBucket]);

  useEffect(() => {
    if (!activeNode) return;
    (async () => {
      setLoadingData(true);
      try {
        await loadProfile();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeNode, loadProfile]);

  // ---------------------------
  // Derived: timestamp options + top 10 dims
  // ---------------------------

  const timestampOptions = useMemo(() => {
    return (timestampBuckets ?? []).map((tb) => ({
      label: formateTimeRange(tb),
      value: tb,
    }));
  }, [timestampBuckets]);

  const topDimensions = useMemo(() => {
    if (!data?.values || !activeTimestampBucket) return [];
    const idx = timestampBuckets.indexOf(activeTimestampBucket);
    if (idx < 0) return [];

    return Object.values(data.values)
      .slice()
      .sort((a, b) => (b?.value?.[idx] ?? 0) - (a?.value?.[idx] ?? 0))
      .filter((dim) => (dim?.value?.[idx] ?? 0) !== 0)
      .slice(0, 10);
  }, [data, activeTimestampBucket, timestampBuckets]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && (
        <div className="w-full flex justify-center">
          <div className="w-full max-w-6xl flex flex-col gap-8">
            {/* ROW 1: Node (left) + Timestamp (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
              {/* Left: Node */}
              <div className="flex flex-col gap-2 items-start">
                <p>Node</p>
                <div className="w-[320px]">
                  <AsyncPaginate
                    value={activeNode}
                    onChange={(selected) => setActiveNode(selected)}
                    loadOptions={loadNodeOptions}
                    additional={nodeAdditional}
                    debounceTimeout={250}
                    cacheOptions
                    placeholder="Type to search…"
                    noOptionsMessage={({ inputValue }) =>
                      (inputValue || "").trim().length < 2
                        ? "Type to search"
                        : "No matches"
                    }
                  />
                </div>
              </div>

              {/* Right: Timestamp Bucket */}
              <div className="flex flex-col gap-2 items-start">
                <p>Timestamp Bucket</p>
                <div className="w-[320px]">
                  <Select
                    isDisabled={!data || loadingBuckets}
                    isSearchable={false}
                    value={
                      activeTimestampBucket
                        ? {
                            label: formateTimeRange(activeTimestampBucket),
                            value: activeTimestampBucket,
                          }
                        : null
                    }
                    onChange={(selected) =>
                      setActiveTimestampBucket(selected?.value ?? null)
                    }
                    options={timestampOptions}
                    placeholder={
                      !activeNode
                        ? "Select a node first…"
                        : loadingBuckets
                        ? "Loading buckets…"
                        : "Select…"
                    }
                  />
                </div>
              </div>
            </div>

            {/* ROW 2: Reset under Node (left only) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="flex items-start">
                <button
                  onClick={resetFiltersUI}
                  className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
                >
                  Reset filters
                </button>
              </div>
              <div />
            </div>

            {/* ROW 3: Profile stats (left) + Top 10 (right) */}
            {data && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* LEFT: Profile Statistics */}
                <div>
                  <h1 className="text-3xl font-bold text-white mb-6">
                    Profile Statistics
                  </h1>

                  {loadingData && <p>Loading data...</p>}

                  {!loadingData && (
                    <div className="bg-qtx-darker shadow-lg rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <thead className="bg-qtx-dark">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                              Profile index
                            </th>
                            {(data?.metadata?.corpora?.time_intervals ?? [])
                              .slice()
                              .reverse()
                              .slice(0, 4)
                              .map((timeInterval, idx) => (
                                <th
                                  key={idx}
                                  className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold"
                                >
                                  {formateTimeRange(timeInterval)}
                                </th>
                              ))}
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-qtx-gray">
                          {[
                            ["Coverage Index", "coverage_index"],
                            ["Intensity Index", "intensity_index"],
                            ["Gini coefficient", "gini_coefficient"],
                            ["Dispersion Index", "dispersion_index"],
                            ["Concentration ratio", "concentration_ratio"],
                          ].map(([label, key]) => (
                            <tr key={key}>
                              <td className="px-6 py-4 text-sm font-mono text-white">
                                {label}
                              </td>
                              {(data?.stats?.[key] ?? [])
                                .slice()
                                .reverse()
                                .slice(0, 4)
                                .map((stat, idx) => (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat.toFixed(2)}
                                  </td>
                                ))}
                            </tr>
                          ))}
                        </tbody>


                      </table>
                    </div>
                  )}
                </div>

                {/* RIGHT: Top 10 */}
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Top 10 Societal Dimensions
                  </h2>

                  {activeTimestampBucket ? (
                    <div className="bg-qtx-darker shadow-lg rounded-lg p-6">
                      <ol className="list-decimal pl-6 space-y-2 text-qtx-light-gray">
                        {topDimensions.map((dimension) => {
                          const idx = timestampBuckets.indexOf(activeTimestampBucket);
                          const v = dimension?.value?.[idx] ?? 0;
                          return (
                            <li key={dimension.label}>
                              <span className="text-white">{dimension.label}</span>{" "}
                              <span className="opacity-80">({v})</span>
                            </li>
                          );
                        })}
                      </ol>

                      {topDimensions.length === 0 && (
                        <p className="text-sm opacity-70 mt-2">
                          No non-zero dimensions for this bucket.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm opacity-70">
                      Select a timestamp bucket to see the top 10.
                    </p>
                  )}
                </div>
              </div>
            )}

            {!data && !loadingData && (
              <p className="text-sm opacity-70">
                Select a node to view profile statistics.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileFilter;